'use client';

import { useEffect, useRef } from 'react';
import { useGameStore } from '@/store/useGameStore';
import { getBadgeRarityColor } from '@/lib/gamification';
import type { Badge } from '@/types';

interface Props {
  badges: Badge[];
}

export function BadgeUnlockModal({ badges }: Props) {
  const pendingBadgeId = useGameStore((s) => s.pendingBadgeId);
  const dismiss = useGameStore((s) => s.dismissBadge);

  const badge = badges.find((b) => b.id === pendingBadgeId);

  if (!pendingBadgeId || !badge) return null;

  return <BadgeModal badge={badge} onDismiss={dismiss} />;
}

interface ModalProps {
  badge: Badge;
  onDismiss: () => void;
}

function BadgeModal({ badge, onDismiss }: ModalProps) {
  const rarityColor = getBadgeRarityColor(badge.rarity);
  const emojiRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (emojiRef.current) {
      emojiRef.current.classList.add('badge-pop');
    }
  }, []);

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      style={{ background: 'rgba(8,8,16,0.85)', backdropFilter: 'blur(8px)' }}
      onClick={onDismiss}
      role="dialog"
      aria-modal="true"
      aria-labelledby="badge-title"
    >
      <div
        className="rounded-2xl p-8 text-center max-w-sm w-full relative"
        style={{
          background: 'var(--color-surface-raised)',
          border: `1px solid ${rarityColor}40`,
          boxShadow: `0 0 40px ${rarityColor}30`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Particle burst (CSS only) */}
        <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none" aria-hidden="true">
          {Array.from({ length: 12 }, (_, i) => (
            <span
              key={i}
              className="absolute w-1.5 h-1.5 rounded-full opacity-0"
              style={{
                background: rarityColor,
                top: '50%',
                left: '50%',
                transform: `rotate(${i * 30}deg) translateY(-60px)`,
                animation: `badge-pop 0.8s ${i * 0.05}s ease-out forwards`,
              }}
            />
          ))}
        </div>

        <p className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-muted)' }}>
          🏅 Badge Unlocked!
        </p>

        <span
          ref={emojiRef}
          className="text-7xl block mb-4"
          role="img"
          aria-label={badge.name}
        >
          {badge.icon}
        </span>

        <h2
          id="badge-title"
          className="font-display font-bold text-xl mb-1"
          style={{ color: 'var(--color-text-primary)' }}
        >
          {badge.name}
        </h2>

        <span
          className="inline-block px-3 py-0.5 rounded-full text-xs font-semibold mb-3 capitalize"
          style={{
            background: `${rarityColor}20`,
            color: rarityColor,
            border: `1px solid ${rarityColor}40`,
          }}
        >
          {badge.rarity}
        </span>

        <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>
          {badge.description}
        </p>

        <p className="font-bold text-lg gradient-text mb-5">
          +{badge.xpBonus} XP Bonus!
        </p>

        <button
          onClick={onDismiss}
          className="btn-primary w-full"
        >
          Awesome! 🎉
        </button>
      </div>
    </div>
  );
}
